import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import FileInput, {File} from "../../../../shared/components/FileInput";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import GlobalAttachmentDialogFields from "../../field/attachment/GlobalAttachmentDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AddToolboxAttachment,
	AdminWebserviceStatus,
	BaseToolbox,
	FileAttachment,
	FileAttachmentInterface,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAttachmentAttachment
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import Exception from "../../../../shared/exception/Exception";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface AttachmentDialogStatus extends PortalDialogState<ToolboxAttachmentAttachment> {
	attachmentFiles: Array<File>;
}

export class GlobalAttachmentDialogDefinition extends PortalDialog<ToolboxAttachmentAttachment,
	PortalDialogProperties<ToolboxAttachmentAttachment>, AttachmentDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			attachmentFiles: [],
			fieldErrors: new Map<GlobalAttachmentDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAttachment, Functions.getEditAttachmentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ATTACHMENT_ADD_GLOBAL, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkPages: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["attachment-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.attachment.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAttachmentAttachment | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let filename = "";
		if (this.state.attachmentFiles.length === 1) {
			filename = this.state.attachmentFiles[0].name
		} else if (this.state.attachmentFiles.length > 1) {
			filename = Translate.value("inputs.file.multiple", {files: this.state.attachmentFiles.length});
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(GlobalAttachmentDialogFields.FILE)
				}>
					<label className="p-col-12 p-sm-4" htmlFor="control-file">
						<Translate {...this.props} data="attachment.properties.file" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8">
						<FileInput inputId="control-file" multiple={true} filename={filename} accept="*/*"
								   onChange={(files) => this.onChange(
									   GlobalAttachmentDialogFields.FILE, files
								   )}/>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: GlobalAttachmentDialogFields, value: any): void {
		let parameter: ToolboxAttachmentAttachment = this.state.parameter!.data;

		switch (field) {
			case GlobalAttachmentDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				parameter.add!.file = [];

				for (let file of value) {
					let operationFileAttachment: FileAttachment = FileAttachment.fromJson({
						fileName: file.name,
						mimeType: file.mimetype,
						data: {
							value: file.value
						}
					} as FileAttachmentInterface);
					parameter.add!.file.push(operationFileAttachment);
				}

				this.setState({
					attachmentFiles: value,
					parameter: this.state.parameter
				});
				return;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-attachment small"}/>
					<Translate {...this.props} data="attachment.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	protected async submit(): Promise<void> {
		let parameter: ToolboxAttachmentAttachment = this.state.parameter!.data;
		if (FunctionHelper.applyValue(parameter.add?.file, AddToolboxAttachment.getFileDefault()).length === 0) {
			this.setState({
				exception: new Exception(Translate.value("errors.files.undefined", {ns: "message"})),
				submitted: false
			});
			return;
		}

		await super.submit();


		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			attachment: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.attachment.add", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxAttachmentAttachment | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		parameter!.add!.file = [];

		this.setState({
			attachmentFiles: [],
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone()
		})
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ATTACHMENT_ADD_GLOBAL;
	}

	protected getDefaultParameter(): Parameter<ToolboxAttachmentAttachment> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			attachmentFiles: []
		})
	}
}

const GlobalAttachmentDialog = React.memo(GlobalAttachmentDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAttachmentAttachment>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAttachmentAttachment>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default GlobalAttachmentDialog;